import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
} from "react-bootstrap";
import { supabase } from "../../config/Supabase";
import "../../styles/General.css";
import { Link } from "react-router-dom";
import SEO from "../utils/SEO";
import { trackContactFormSubmit } from "../../utils/analytics";

const Contact = () => {
  const [selectedReason, setSelectedReason] = useState("");
  const [senderFullName, setSenderFullName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const validateForm = () => {
    if (!selectedReason) {
      setError("Please select a reason for contacting us.");
      return false;
    }
    if (!senderFullName) {
      setError("Please enter your full name.");
      return false;
    }
    if (!senderEmail || !/\S+@\S+\.\S+/.test(senderEmail)) {
      setError("Please enter a valid email address.");
      return false;
    }
    if (!message) {
      setError("Please enter a message.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setError(""); // Reset error message
    setSuccess("");
    setSubmitting(true);

    try {
      // Store in Supabase (public anon INSERT — see the contact_messages RLS in
      // supabase/migrations/20260727000000_contact_messages.sql). This replaced
      // the old Firestore "contacts" collection after the Supabase migration.
      const { error: insertError } = await supabase
        .from("contact_messages")
        .insert({
          contact_reason: selectedReason,
          sender_full_name: senderFullName.trim(),
          sender_email: senderEmail.trim().toLowerCase(),
          message: message.trim(),
          source: window.location.pathname,
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent : null,
        });

      if (insertError) throw insertError;

      trackContactFormSubmit(selectedReason, true);
      setSelectedReason("");
      setSenderFullName("");
      setSenderEmail("");
      setMessage("");
      setSuccess(
        "Thanks for reaching out! Our team has received your message and will get back to you soon."
      );
    } catch (submitError) {
      console.error("Error submitting contact form:", submitError);
      trackContactFormSubmit(selectedReason, false);
      setError("Failed to submit the form. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      className="contact-form-background d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh" }}
    >
      <SEO
        title="Contact Us - Sarv Holistic Healthcare"
        description="Get in touch with the Sarv team. We're here to help with questions about personalized herbal medicine, subscriptions, partnerships, or general inquiries."
        canonicalPath="/contact"
        keywords="contact Sarv, holistic health support, herbal medicine help, Sarv customer service"
      />
      <div className="w-100" style={{ maxWidth: "900px" }}>
        <p
          className="text-center"
          style={{ fontSize: "36px", fontWeight: "bold", paddingTop: "100px" }}
        >
          Contact us
        </p>
        <p style={{ fontSize: "20px", paddingTop: "30px" }}>
          Use this form to reach out to our Customer Success Team regarding any
          questions, concerns, or feedback. You can also view our{" "}
          <Link to="/faq">FAQ</Link> for quick answers to commonly asked
          questions.
        </p>{" "}
        {/* Contact Form */}
        <Card style={{ border: "none" }}>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              {/* Radio Buttons for Reason */}
              <Form.Group style={{ paddingTop: "50px" }}>
                <Form.Label style={{ fontSize: "20px", paddingBottom: "25px" }}>
                  Reason for contacting:
                </Form.Label>
                {[
                  "I am a registered client and I need support.",
                  "I am a current Sarv practitioner and I need support.",
                  "I am a practitioner interested in joining or a current applicant.",
                  "I have a question about the service.",
                  "I have a billing related question.",
                  "I have a press related question.",
                  "I have a business related inquiry.",
                  "I am interested in Sarv for my organization.",
                ].map((reason, idx) => (
                  <Form.Check
                    key={idx}
                    type="radio"
                    label={reason}
                    name="contactReason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="mb-2"
                  />
                ))}
              </Form.Group>

              {/* Validation Error Alert */}

              {/* Full Name Field */}
              <Form.Group
                controlId="formFullName"
                style={{ paddingTop: "50px" }}
              >
                <Form.Control
                  type="text"
                  placeholder="Full name"
                  style={{ marginBottom: "15px" }}
                  value={senderFullName}
                  onChange={(e) => setSenderFullName(e.target.value)}
                />
              </Form.Group>

              {/* Email Field */}
              <Form.Group controlId="formBasicEmail">
                <Form.Control
                  type="email"
                  placeholder="Email address"
                  style={{ marginBottom: "20px" }}
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                />
              </Form.Group>

              {/* Message Box */}
              <Form.Group controlId="formMessage">
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Your message"
                  style={{ marginBottom: "10px", height: "250px" }}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Form.Group>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              {/* Submit Button */}
              <div
                className="d-flex justify-content-center"
                style={{ paddingBottom: "100px", paddingTop: "30px" }}
              >
                <Button
                  variant="primary"
                  type="submit"
                  className="text-center custom-subscribe-btn"
                  disabled={submitting}
                >
                  {submitting ? "Submitting…" : "Submit"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default Contact;
